// =============================================
// Call Center Dashboard v2 - מוקד חרום
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, Clock, CheckCircle, XCircle, Eye,
  Phone, PhoneCall, MessageSquare, Mail, FileText,
  ChevronDown, ChevronUp, Loader2, Circle,
  Building2, User, Shield, Landmark, Send, X,
  Search, Filter, Radio, Calendar,
} from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import {
  callCenterApi,
  type CallCenterAlertDto,
  type CallCenterStats,
} from '../../services/api';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { callCenterHelpTexts } from '../../constants/helpTexts';
import { CallDocumentModal } from './CallDocumentModal';
import { callCenterColors as colors } from '../../styles/colors';

// Context from CallCenterLayout for org filtering
interface CallCenterOutletContext {
  selectedOrganizationId: number | undefined;
  setOrganizations: (orgs: { id: number; name: string }[]) => void;
}

type TabFilter = 'inProgress' | 'needsAction' | 'level2' | 'level1' | 'completed' | 'all';

interface CallDocState {
  alertId: string;
  contactName: string;
  contactPhone: string;
  actionType: string;
  note: string;
}

interface MessagingFlowState {
  alertId: string;
  method: 'WhatsApp' | 'SMS' | 'Email';
  step: 'pickContact' | 'document';
  contactName?: string;
  contactPhone?: string;
  note: string;
}

export default function CallCenterDashboard() {
  const outletContext = useOutletContext<CallCenterOutletContext | undefined>();
  const selectedOrganizationId = outletContext?.selectedOrganizationId;
  const setOrganizations = outletContext?.setOrganizations;

  const [stats, setStats] = useState<CallCenterStats | null>(null);
  const [alerts, setAlerts] = useState<CallCenterAlertDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('needsAction');
  const [expandedAlertId, setExpandedAlertId] = useState<string | null>(null);
  const [resolutionText, setResolutionText] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [updatingAlertId, setUpdatingAlertId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [callDoc, setCallDoc] = useState<CallDocState | null>(null);
  const [closingAlertId, setClosingAlertId] = useState<string | null>(null);
  const [messagingFlow, setMessagingFlow] = useState<MessagingFlowState | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Format yyyy-MM-dd to dd/MM/yyyy
  const fmtDate = (d: string) => {
    if (!d) return '';
    const p = d.split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
  };

  // Today's date as yyyy-MM-dd for max attribute
  const todayStr = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  // History mode filters
  const [isHistoryMode, setIsHistoryMode] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [kindergartenFilter, setKindergartenFilter] = useState('');
  const [timeFrom, setTimeFrom] = useState('06:30');
  const [timeTo, setTimeTo] = useState('12:30');
  const [showFilters, setShowFilters] = useState(false);
  const [showRangeWarning, setShowRangeWarning] = useState(false);
  const [pendingSearch, setPendingSearch] = useState(false);

  // Auto-refresh every 15 seconds (disabled in history mode)
  useEffect(() => {
    if (isHistoryMode) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }
    intervalRef.current = setInterval(() => {
      setRefreshKey(k => k + 1);
    }, 15000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isHistoryMode]);

  // Helper: convert yyyy-MM-dd to yyyyMMdd
  const toApiDate = (htmlDate: string): string => htmlDate.replace(/-/g, '');

  // Load data (stale closure safe)
  useEffect(() => {
    const loadData = async () => {
      try {
        if (refreshKey > 0) setRefreshing(true);
        setRefreshError(false);

        const alertParams: { from?: string; to?: string } = {};
        if (isHistoryMode && dateFrom) alertParams.from = toApiDate(dateFrom);
        if (isHistoryMode && dateTo) alertParams.to = toApiDate(dateTo);

        const [statsResult, alertsResult] = await Promise.all([
          callCenterApi.getStats(),
          callCenterApi.getAlerts(alertParams),
        ]);

        if (statsResult.success && statsResult.data) setStats(statsResult.data);
        if (alertsResult.success && alertsResult.data) {
          setAlerts(alertsResult.data);
          // Extract unique organizations for sidebar filter
          if (setOrganizations) {
            const orgMap = new Map<number, string>();
            alertsResult.data.forEach(a => {
              if (a.organizationId && a.kindergartenName) {
                orgMap.set(a.organizationId, a.kindergartenName.split(' - ')[0]); // rough org name extraction
              }
            });
            // Better: use organizationId as key, derive name from kindergartenName
            setOrganizations(Array.from(orgMap.entries()).map(([id, name]) => ({ id, name: `ארגון ${id}` })));
          }
        }
        setLastRefreshTime(new Date());
      } catch (err) {
        console.error('Failed to load call center data:', err);
        setRefreshError(true);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };

    loadData();
  }, [refreshKey, isHistoryMode, dateFrom, dateTo]);

  // Pre-filter by organization (if selected)
  const orgFilteredAlerts = selectedOrganizationId
    ? alerts.filter(a => a.organizationId === selectedOrganizationId)
    : alerts;

  // Filter by search query (name, kindergarten)
  const searchFiltered = searchQuery.trim()
    ? orgFilteredAlerts.filter(a => {
        const q = searchQuery.trim().toLowerCase();
        return a.childName.toLowerCase().includes(q)
          || a.kindergartenName.toLowerCase().includes(q);
      })
    : orgFilteredAlerts;

  // Filter by kindergarten name
  const kgFiltered = kindergartenFilter
    ? searchFiltered.filter(a => a.kindergartenName === kindergartenFilter)
    : searchFiltered;

  // Filter by time range (client-side, on alertTime) — compare HH:MM strings
  const timeFiltered = (timeFrom || timeTo)
    ? kgFiltered.filter(a => {
        const match = String(a.alertTime).match(/T(\d{2}:\d{2})/);
        if (!match) return true;
        const hhmm = match[1];
        if (timeFrom && hhmm < timeFrom) return false;
        if (timeTo && hhmm > timeTo) return false;
        return true;
      })
    : kgFiltered;

  // Unique kindergarten names for dropdown
  const uniqueKindergartens = Array.from(new Set(orgFilteredAlerts.map(a => a.kindergartenName))).sort();

  // Filter alerts by tab
  const filteredAlerts = timeFiltered.filter(a => {
    switch (activeTab) {
      case 'inProgress': return a.status === 'InProgress';
      case 'needsAction': return a.status === 'Open';
      case 'level2': return a.status === 'Watching' && a.alertLevel === 2;
      case 'level1': return a.status === 'Watching' && a.alertLevel === 1;
      case 'completed': return a.status === 'Resolved' || a.status === 'FalseAlarm';
      case 'all': return true;
      default: return true;
    }
  });

  // Count by category (from all filtered except tab, not tab-filtered)
  const inProgressCount = timeFiltered.filter(a => a.status === 'InProgress').length;
  const needsActionCount = timeFiltered.filter(a => a.status === 'Open').length;
  const level2Count = timeFiltered.filter(a => a.status === 'Watching' && a.alertLevel === 2).length;
  const level1Count = timeFiltered.filter(a => a.status === 'Watching' && a.alertLevel === 1).length;
  const completedCount = timeFiltered.filter(a => a.status === 'Resolved' || a.status === 'FalseAlarm').length;

  // Handlers
  const handlePickUp = async (alert: CallCenterAlertDto) => {
    setUpdatingAlertId(alert.alertId);
    try {
      await callCenterApi.updateAlertStatus(alert.date, alert.alertId, {
        status: 'InProgress',
      });
      setRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Failed to pick up alert:', err);
    } finally {
      setUpdatingAlertId(null);
    }
  };

  const handleResolve = async (alert: CallCenterAlertDto, status: 'Resolved' | 'FalseAlarm') => {
    if (!resolutionText.trim()) return;
    setUpdatingAlertId(alert.alertId);
    try {
      await callCenterApi.updateAlertStatus(alert.date, alert.alertId, {
        status,
        resolution: resolutionText.trim(),
      });
      setResolutionText('');
      setExpandedAlertId(null);
      setRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    } finally {
      setUpdatingAlertId(null);
    }
  };

  const handleAddAction = async (alert: CallCenterAlertDto, action: string, details: string) => {
    setUpdatingAlertId(alert.alertId);
    try {
      await callCenterApi.addActionLog(alert.date, alert.alertId, {
        action,
        details: details || action,
      });
      setActionNote('');
      setRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Failed to add action:', err);
    } finally {
      setUpdatingAlertId(null);
    }
  };

  // Call documentation flow
  const startCallDoc = (alert: CallCenterAlertDto, contactName: string, contactPhone: string, actionType: string) => {
    // Open tel: link
    window.open(`tel:${contactPhone}`, '_self');
    // Show documentation form
    setCallDoc({ alertId: alert.alertId, contactName, contactPhone, actionType, note: '' });
  };

  const saveCallDoc = async (alert: CallCenterAlertDto) => {
    if (!callDoc || !callDoc.note.trim()) return;
    const details = `שיחה עם ${callDoc.contactName} (${callDoc.contactPhone}) — ${callDoc.note.trim()}`;
    await handleAddAction(alert, callDoc.actionType, details);
    setCallDoc(null);
  };

  // Messaging flow
  const startMessaging = (alertId: string, method: 'WhatsApp' | 'SMS' | 'Email') => {
    setMessagingFlow({ alertId, method, step: 'pickContact', note: '' });
  };

  const selectMessagingContact = (name: string, phone: string) => {
    if (!messagingFlow) return;
    setMessagingFlow({ ...messagingFlow, step: 'document', contactName: name, contactPhone: phone, note: '' });
  };

  const saveMessaging = async (alert: CallCenterAlertDto) => {
    if (!messagingFlow || !messagingFlow.note.trim()) return;
    const methodLabel = messagingFlow.method === 'WhatsApp' ? 'WhatsApp' : messagingFlow.method === 'SMS' ? 'SMS' : 'Email';
    const actionType = `Sent${messagingFlow.method}`;
    const details = `${methodLabel} ל-${messagingFlow.contactName} (${messagingFlow.contactPhone || ''}) — ${messagingFlow.note.trim()}`;
    await handleAddAction(alert, actionType, details);
    setMessagingFlow(null);
  };

  const getAlertLevelInfo = (level: number) => {
    switch (level) {
      case 1: return { label: 'הודעה ראשונה נשלחה', color: colors.info, bg: colors.infoLight, border: colors.infoBorder };
      case 2: return { label: 'הודעה שנייה נשלחה', color: colors.orange, bg: colors.orangeLight, border: colors.orangeBorder };
      case 3: return { label: 'דורש טיפול מוקד', color: colors.danger, bg: colors.dangerLight, border: colors.dangerBorder };
      default: return { label: '', color: colors.muted, bg: colors.mutedLight, border: colors.border };
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Watching': return { label: 'מעקב', color: colors.info, bg: colors.infoLight };
      case 'Open': return { label: 'ממתין לטיפול', color: colors.danger, bg: colors.dangerLight };
      case 'InProgress': return { label: 'בטיפול', color: colors.warning, bg: colors.warningLight };
      case 'Resolved': return { label: 'טופל', color: colors.success, bg: colors.successLight };
      case 'FalseAlarm': return { label: 'אזעקת שווא', color: colors.muted, bg: colors.mutedLight };
      default: return { label: status, color: colors.muted, bg: colors.mutedLight };
    }
  };

  // IMPORTANT: Backend stores Israel time but labels as UTC (SpecifyKind).
  // Using new Date() would add +2/+3h. Extract HH:mm directly via regex.
  const formatTime = (timeStr: string | undefined | null) => {
    if (!timeStr) return '';
    const match = String(timeStr).match(/T(\d{2}:\d{2})/);
    return match ? match[1] : '';
  };

  // Translate action types to Hebrew with icons for timeline
  const translateAction = (action: string): string => {
    switch (action) {
      case 'SentFirstAlert': return '📱 הודעה ראשונה נשלחה';
      case 'SentSecondAlert': return '📱 הודעה שנייה נשלחה';
      case 'EscalatedToCallCenter': return '🔴 הועבר למוקד חירום';
      case 'CalledParent': return '📞 שיחה עם הורה';
      case 'CalledKindergarten': return '📞 שיחה לגן';
      case 'CalledPolice': return '🚨 שיחה למוקד משטרה';
      case 'CalledLocalEmergency': return '🚨 שיחה למוקד חירום';
      case 'SentWhatsApp': return '💬 WhatsApp נשלח';
      case 'SentSMS': return '💬 SMS נשלח';
      case 'SentEmail': return '📧 Email נשלח';
      case 'Note': return '📝 הערה';
      default: return action;
    }
  };

  // Format alert time as dd/MM/yyyy HH:mm — extract directly to avoid timezone double-conversion
  const formatAlertDateTime = (timeStr: string) => {
    if (!timeStr) return '';
    try {
      // Extract date and time parts directly from ISO string
      const dateMatch = String(timeStr).match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/);
      if (dateMatch) {
        const [, year, month, day, time] = dateMatch;
        return `${day}/${month}/${year} ${time}`;
      }
      return '';
    } catch { return ''; }
  };

  const calcDuration = (from: string, to: string) => {
    try {
      const d1 = new Date(from);
      const d2 = new Date(to);
      const diffMin = Math.floor((d2.getTime() - d1.getTime()) / 60000);
      if (diffMin < 1) return 'פחות מדקה';
      if (diffMin < 60) return `${diffMin} דקות`;
      const hours = Math.floor(diffMin / 60);
      const mins = diffMin % 60;
      return mins > 0 ? `${hours} שע' ${mins} דק'` : `${hours} שעות`;
    } catch { return ''; }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Loader2 size={40} color={colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header with live indicator */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '0' }}>
              לוח בקרה - מוקד חרום
            </h1>
            {/* Live indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {refreshError ? (
                <>
                  <Circle size={10} fill={colors.danger} color={colors.danger} />
                  <span style={{ fontSize: '11px', color: colors.danger }}>
                    שגיאה ברענון
                  </span>
                  <button
                    onClick={() => setRefreshKey(k => k + 1)}
                    style={{
                      fontSize: '11px', color: colors.primary, cursor: 'pointer',
                      border: 'none', background: 'none', textDecoration: 'underline', padding: 0,
                    }}
                  >
                    נסה שוב
                  </button>
                </>
              ) : (
                <>
                  <div style={{
                    width: '8px', height: '8px', borderRadius: '50%',
                    backgroundColor: colors.success,
                    animation: refreshing ? 'none' : 'pulse 2s ease-in-out infinite',
                  }} />
                  <span style={{ fontSize: '11px', color: colors.textLight }}>
                    {refreshing ? 'מרענן...' : lastRefreshTime ? `עודכן: ${lastRefreshTime.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : ''}
                  </span>
                </>
              )}
            </div>
          </div>
          <p style={{ fontSize: '14px', color: colors.textLight, marginTop: '4px' }}>
            {new Date().toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>

      {/* Stats Cards — 5 cards matching new tabs */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
          marginBottom: '24px',
        }}>
          {[
            { label: 'דרוש טיפול', value: stats.openAlerts, icon: AlertTriangle, color: colors.danger, bg: colors.dangerLight },
            { label: 'בטיפול', value: stats.inProgressAlerts, icon: Clock, color: colors.warning, bg: colors.warningLight },
            { label: 'הודעה שנייה', value: stats.level2Watching, icon: Eye, color: colors.orange, bg: colors.orangeLight },
            { label: 'הודעה ראשונה', value: stats.level1Watching, icon: Eye, color: colors.info, bg: colors.infoLight },
            { label: 'טופלו היום', value: stats.resolvedToday + stats.falseAlarmToday, icon: CheckCircle, color: colors.success, bg: colors.successLight },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              style={{
                padding: '16px',
                borderRadius: '14px',
                backgroundColor: colors.white,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  backgroundColor: stat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <stat.icon size={18} color={stat.color} />
                </div>
                <span style={{ fontSize: '13px', color: colors.textLight }}>{stat.label}</span>
              </div>
              <p style={{ fontSize: '28px', fontWeight: 'bold', color: stat.color }}>{stat.value}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Search Bar */}
      <FormLabel text="חיפוש" helpText={callCenterHelpTexts.search.tooltip} style={{ color: colors.text }} />
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
        padding: '8px 12px',
        borderRadius: '10px',
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
      }}>
        <Search size={16} color={colors.textLight} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="חיפוש לפי שם ילד או גן..."
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontSize: '14px',
            direction: 'rtl',
            backgroundColor: 'transparent',
            color: colors.text,
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px' }}
          >
            <X size={14} color={colors.textLight} />
          </button>
        )}
      </div>

      {/* History Mode Toggle + Advanced Filters */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: showFilters ? '10px' : '0' }}>
          <button
            onClick={() => {
              if (isHistoryMode) {
                setIsHistoryMode(false);
                setDateFrom('');
                setDateTo('');
                setKindergartenFilter('');
                setTimeFrom('06:30');
                setTimeTo('12:30');
                setShowFilters(false);
                setRefreshKey(k => k + 1);
              } else {
                setShowFilters(!showFilters);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '10px',
              border: isHistoryMode ? `2px solid ${colors.primary}` : `1px solid ${colors.border}`,
              backgroundColor: isHistoryMode ? colors.primaryLight : colors.white,
              color: isHistoryMode ? colors.primary : colors.textLight,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: isHistoryMode ? '600' : '400',
            }}
          >
            {isHistoryMode ? <><Radio size={14} /> {'מצב היסטוריה - לחץ לחזרה למצב חי'}</> : <><Filter size={14} /> {'חיפוש היסטוריה'}</>}
          </button>
          {isHistoryMode && (
            <span style={{ fontSize: '12px', color: colors.textLight }}>
              {'רענון אוטומטי מושבת'}
            </span>
          )}
        </div>

        <AnimatePresence>
          {(showFilters || isHistoryMode) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                padding: '14px',
                borderRadius: '12px',
                backgroundColor: colors.white,
                border: `1px solid ${colors.border}`,
                direction: 'rtl',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, fontWeight: '500' }}>{'מתאריך'}</label>
                  <div style={{ position: 'relative', minWidth: '140px' }}>
                    <input
                      type="date"
                      value={dateFrom}
                      max={todayStr}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="date-input-overlay"
                      style={{
                        padding: '7px 10px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.border}`,
                        fontSize: '13px',
                        color: 'transparent',
                        backgroundColor: colors.white,
                        width: '100%',
                        cursor: 'pointer',
                        direction: 'ltr',
                      }}
                    />
                    <div style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '13px', color: dateFrom ? colors.text : colors.textLight, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={13} color={colors.textLight} />
                      {dateFrom ? fmtDate(dateFrom) : 'dd/mm/yyyy'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, fontWeight: '500' }}>{'עד תאריך'}</label>
                  <div style={{ position: 'relative', minWidth: '140px' }}>
                    <input
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      max={todayStr}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="date-input-overlay"
                      style={{
                        padding: '7px 10px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.border}`,
                        fontSize: '13px',
                        color: 'transparent',
                        backgroundColor: colors.white,
                        width: '100%',
                        cursor: 'pointer',
                        direction: 'ltr',
                      }}
                    />
                    <div style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '13px', color: dateTo ? colors.text : colors.textLight, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={13} color={colors.textLight} />
                      {dateTo ? fmtDate(dateTo) : 'dd/mm/yyyy'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, fontWeight: '500' }}>{'גן'}</label>
                  <select
                    value={kindergartenFilter}
                    onChange={(e) => setKindergartenFilter(e.target.value)}
                    style={{
                      padding: '7px 10px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      fontSize: '13px',
                      color: colors.text,
                      minWidth: '140px',
                      backgroundColor: colors.white,
                    }}
                  >
                    <option value="">{'הכל'}</option>
                    {uniqueKindergartens.map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, fontWeight: '500' }}>{'משעה'}</label>
                  <select
                    value={timeFrom}
                    onChange={(e) => setTimeFrom(e.target.value)}
                    style={{
                      padding: '7px 10px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      fontSize: '13px',
                      color: colors.text,
                      backgroundColor: colors.white,
                    }}
                  >
                    <option value="">{'--'}</option>
                    {(() => { const opts = []; for (let h = 6; h <= 18; h++) for (let m = 0; m < 60; m += 5) opts.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`); return opts; })().map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, fontWeight: '500' }}>{'עד שעה'}</label>
                  <select
                    value={timeTo}
                    onChange={(e) => setTimeTo(e.target.value)}
                    style={{
                      padding: '7px 10px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      fontSize: '13px',
                      color: colors.text,
                      backgroundColor: colors.white,
                    }}
                  >
                    <option value="">{'--'}</option>
                    {(() => { const opts = []; for (let h = 6; h <= 18; h++) for (let m = 0; m < 60; m += 5) opts.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`); return opts; })().map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => {
                    if (dateFrom && dateTo) {
                      const d1 = new Date(dateFrom);
                      const d2 = new Date(dateTo);
                      const diffDays = (d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24);
                      if (diffDays > 31) {
                        setShowRangeWarning(true);
                        setPendingSearch(true);
                        return;
                      }
                    }
                    setIsHistoryMode(true);
                    setRefreshKey(k => k + 1);
                  }}
                  style={{
                    padding: '7px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: colors.primary,
                    color: colors.white,
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Search size={14} />
                  {'חיפוש'}
                </button>
                {isHistoryMode && (
                  <button
                    onClick={() => {
                      setKindergartenFilter('');
                      setTimeFrom('');
                      setTimeTo('');
                    }}
                    style={{
                      padding: '7px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.white,
                      color: colors.textLight,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    {'נקה סינון'}
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Range Warning Modal */}
      <AnimatePresence>
        {showRangeWarning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowRangeWarning(false); setPendingSearch(false); } }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                backgroundColor: colors.white,
                borderRadius: '16px',
                padding: '28px',
                maxWidth: '400px',
                width: '90%',
                textAlign: 'center',
                direction: 'rtl',
              }}
            >
              <AlertTriangle size={40} color={colors.warning} style={{ marginBottom: '12px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: colors.text, marginBottom: '8px' }}>
                {'טווח תאריכים גדול'}
              </h3>
              <p style={{ fontSize: '14px', color: colors.textLight, marginBottom: '20px', lineHeight: '1.6' }}>
                {'טווח התאריכים שנבחר גדול מחודש. שליפת נתונים בטווח גדול עשויה להיות איטית מאוד. מומלץ לבצע חיפוש בטווח הקטן מחודש.'}
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    setShowRangeWarning(false);
                    setPendingSearch(false);
                    setIsHistoryMode(true);
                    setRefreshKey(k => k + 1);
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: colors.warning,
                    color: colors.white,
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  {'המשך בכל זאת'}
                </button>
                <button
                  onClick={() => { setShowRangeWarning(false); setPendingSearch(false); }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.white,
                    color: colors.textLight,
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  {'ביטול'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tab Filters — 6 tabs */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '6px' }}>
        <FormLabel text="סינון סטטוס" helpText={callCenterHelpTexts.statusFilter.tooltip} style={{ color: colors.text }} />
        <FormLabel text="סינון דחיפות" helpText={callCenterHelpTexts.priorityFilter.tooltip} style={{ color: colors.text }} />
      </div>
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        flexWrap: 'wrap',
      }}>
        {([
          { key: 'needsAction' as TabFilter, label: 'דרוש טיפול', count: needsActionCount, color: colors.danger },
          { key: 'inProgress' as TabFilter, label: 'בטיפול', count: inProgressCount, color: colors.warning },
          { key: 'level2' as TabFilter, label: 'הודעה שנייה', count: level2Count, color: colors.orange },
          { key: 'level1' as TabFilter, label: 'הודעה ראשונה', count: level1Count, color: colors.info },
          { key: 'completed' as TabFilter, label: 'הושלם', count: completedCount, color: colors.success },
          { key: 'all' as TabFilter, label: 'הכל', count: timeFiltered.length, color: colors.muted },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: activeTab === tab.key ? `2px solid ${tab.color}` : `1px solid ${colors.border}`,
              backgroundColor: activeTab === tab.key ? `${tab.color}15` : colors.white,
              color: activeTab === tab.key ? tab.color : colors.textLight,
              fontWeight: activeTab === tab.key ? '600' : '400',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {tab.label}
            <span style={{
              backgroundColor: activeTab === tab.key ? tab.color : colors.mutedLight,
              color: activeTab === tab.key ? colors.white : colors.muted,
              borderRadius: '10px',
              padding: '1px 8px',
              fontSize: '12px',
              fontWeight: '600',
            }}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Alerts List */}
      {filteredAlerts.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: colors.white,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
        }}>
          <CheckCircle size={48} color={colors.success} style={{ marginBottom: '12px' }} />
          <p style={{ fontSize: '16px', color: colors.textLight }}>
            {activeTab === 'needsAction' ? 'אין התראות הממתינות לטיפול' :
             activeTab === 'inProgress' ? 'אין התראות בטיפול כרגע' :
             'אין התראות להצגה'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filteredAlerts.map((alert, index) => {
            const isExpanded = expandedAlertId === alert.alertId;
            const levelInfo = getAlertLevelInfo(alert.alertLevel);
            const statusBadge = getStatusBadge(alert.status);
            const isUpdating = updatingAlertId === alert.alertId;
            const isCompleted = alert.status === 'Resolved' || alert.status === 'FalseAlarm';

            return (
              <motion.div
                key={alert.alertId}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                style={{
                  borderRadius: '14px',
                  backgroundColor: colors.white,
                  border: `2px solid ${levelInfo.border}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  opacity: isCompleted ? 0.7 : 1,
                }}
              >
                {/* Alert Header (always visible) */}
                <div
                  onClick={() => setExpandedAlertId(isExpanded ? null : alert.alertId)}
                  style={{
                    padding: '14px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    backgroundColor: alert.status === 'Open' && !isCompleted ? levelInfo.bg : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <div style={{
                      width: '8px', height: '40px', borderRadius: '4px',
                      backgroundColor: levelInfo.color, flexShrink: 0,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '600', fontSize: '15px', color: colors.text }}>
                          {alert.childName}
                        </span>
                        <span style={{
                          padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600',
                          backgroundColor: statusBadge.bg, color: statusBadge.color,
                        }}>
                          {statusBadge.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px', fontSize: '13px', color: colors.textLight }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Building2 size={12} /> {alert.kindergartenName}
                        </span>
                        <span>{formatAlertDateTime(alert.alertTime)}</span>
                        <span style={{ fontSize: '12px', color: levelInfo.color }}>{levelInfo.label}</span>
                      </div>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp size={18} color={colors.textLight} /> : <ChevronDown size={18} color={colors.textLight} />}
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      style={{
                        padding: '0 16px 16px',
                        borderTop: `1px solid ${colors.border}`,
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{ paddingTop: '14px' }}>

                        {/* ===== COMPLETED CARDS: Resolution Summary ===== */}
                        {isCompleted && (
                          <div style={{
                            padding: '16px',
                            borderRadius: '12px',
                            backgroundColor: alert.status === 'FalseAlarm' ? colors.mutedLight : colors.successLight,
                            borderRight: `4px solid ${alert.status === 'FalseAlarm' ? colors.muted : colors.success}`,
                            marginBottom: '16px',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                              {alert.status === 'FalseAlarm' ? (
                                <XCircle size={20} color={colors.muted} />
                              ) : (
                                <CheckCircle size={20} color={colors.success} />
                              )}
                              <span style={{
                                fontSize: '15px', fontWeight: '700',
                                color: alert.status === 'FalseAlarm' ? colors.muted : colors.success,
                              }}>
                                {alert.status === 'FalseAlarm' ? 'אזעקת שווא' : 'טופל בהצלחה'}
                              </span>
                            </div>

                            <div style={{ fontSize: '14px', color: colors.text, marginBottom: '8px' }}>
                              <span style={{ fontWeight: '600' }}>סיבת סגירה: </span>
                              <span style={{ fontSize: '15px' }}>{alert.resolution || '—'}</span>
                            </div>

                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: colors.textLight }}>
                              {alert.assignedTo && (
                                <span>מטפל: <b>{alert.assignedTo}</b></span>
                              )}
                              {alert.resolvedAt && (
                                <span>נסגר בשעה: <b>{formatTime(alert.resolvedAt)}</b></span>
                              )}
                              {alert.alertTime && alert.resolvedAt && (
                                <span>משך טיפול: <b>{calcDuration(alert.alertTime, alert.resolvedAt)}</b></span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* ===== OPEN (דרוש טיפול): Pick Up Button ===== */}
                        {alert.status === 'Open' && (
                          <div style={{ marginBottom: '16px' }}>
                            <button
                              onClick={() => handlePickUp(alert)}
                              disabled={isUpdating}
                              style={{
                                width: '100%',
                                padding: '14px',
                                borderRadius: '10px',
                                border: 'none',
                                backgroundColor: colors.danger,
                                color: colors.white,
                                fontSize: '16px',
                                fontWeight: '700',
                                cursor: isUpdating ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                              }}
                            >
                              {isUpdating ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <PhoneCall size={18} />}
                              {isUpdating ? 'מעדכן...' : 'קבל טיפול — התחל לטפל בהתראה'}
                            </button>
                          </div>
                        )}

                        {/* Contact Info Grid */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                          gap: '12px',
                          marginBottom: '16px',
                          opacity: isCompleted ? 0.6 : 1,
                        }}>
                          {/* Kindergarten */}
                          <div style={{
                            padding: '12px', borderRadius: '10px',
                            backgroundColor: colors.mutedLight, border: `1px solid ${colors.border}`,
                          }}>
                            <p style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px' }}>גן</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: colors.text }}>{alert.kindergartenName}</p>
                            {alert.kindergartenPhone && (
                              <a href={`tel:${alert.kindergartenPhone}`} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                color: colors.primary, fontSize: '13px', marginTop: '4px', textDecoration: 'none',
                              }}>
                                <Phone size={12} /> {alert.kindergartenPhone}
                              </a>
                            )}
                          </div>
                          {/* Parents */}
                          {alert.parentContacts.map((parent, i) => (
                            <div key={i} style={{
                              padding: '12px', borderRadius: '10px',
                              backgroundColor: colors.mutedLight, border: `1px solid ${colors.border}`,
                            }}>
                              <p style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px' }}>
                                <User size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> הורה {i + 1}
                              </p>
                              <p style={{ fontWeight: '600', fontSize: '14px', color: colors.text }}>{parent.name}</p>
                              <a href={`tel:${parent.phone}`} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                color: colors.primary, fontSize: '13px', marginTop: '4px', textDecoration: 'none',
                              }}>
                                <Phone size={12} /> {parent.phone}
                              </a>
                            </div>
                          ))}
                        </div>

                        {/* Times — with highlighted expected arrival */}
                        <div style={{
                          display: 'flex', gap: '12px', marginBottom: '16px', fontSize: '13px', color: colors.textLight,
                          flexWrap: 'wrap', alignItems: 'center',
                        }}>
                          {/* Highlighted expected arrival time */}
                          <span style={{
                            backgroundColor: colors.warningLight,
                            border: `1px solid ${colors.warningBorder}`,
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '14px',
                            color: colors.text,
                          }}>
                            שעת הגעה צפויה: {formatTime(alert.expectedArrivalTime)}
                          </span>
                          <span>שעת התראה: <b>{formatTime(alert.alertTime)}</b></span>
                          {alert.pickedUpAt && <span>נלקח לטיפול: <b>{formatTime(alert.pickedUpAt)}</b></span>}
                          {alert.assignedTo && !isCompleted && <span>מטפל: <b>{alert.assignedTo}</b></span>}
                          {alert.resolvedAt && !isCompleted && <span>נסגר: <b>{formatTime(alert.resolvedAt)}</b></span>}
                        </div>

                        {/* ===== ACTION SECTION (Level 3 InProgress) ===== */}
                        {alert.status === 'InProgress' && (
                          <div style={{ marginBottom: '16px' }}>
                            <p style={{ fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '10px' }}>
                              תיעוד פעולות:
                            </p>

                            {/* Call Buttons — with mandatory documentation */}
                            <div style={{ marginBottom: '12px' }}>
                              <p style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px' }}>שיחות טלפון:</p>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {alert.parentContacts.map((parent, i) => (
                                  <button
                                    key={`call-${i}`}
                                    onClick={() => startCallDoc(alert, parent.name, parent.phone, 'CalledParent')}
                                    disabled={isUpdating}
                                    style={{
                                      padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`,
                                      backgroundColor: colors.white, cursor: 'pointer', fontSize: '12px',
                                      display: 'flex', alignItems: 'center', gap: '4px',
                                    }}
                                  >
                                    <PhoneCall size={12} color={colors.primary} /> שיחה - {parent.name}
                                  </button>
                                ))}
                                <button
                                  onClick={() => startCallDoc(alert, alert.kindergartenName, alert.kindergartenPhone, 'CalledKindergarten')}
                                  disabled={isUpdating}
                                  style={{
                                    padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`,
                                    backgroundColor: colors.white, cursor: 'pointer', fontSize: '12px',
                                    display: 'flex', alignItems: 'center', gap: '4px',
                                  }}
                                >
                                  <Building2 size={12} color={colors.primary} /> שיחה לגן
                                </button>
                                <button
                                  onClick={() => startCallDoc(alert, 'מוקד משטרה', '100', 'CalledPolice')}
                                  disabled={isUpdating}
                                  style={{
                                    padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.dangerBorder}`,
                                    backgroundColor: colors.dangerLight, cursor: 'pointer', fontSize: '12px',
                                    display: 'flex', alignItems: 'center', gap: '4px', color: colors.danger,
                                  }}
                                >
                                  <Shield size={12} /> מוקד משטרה
                                </button>
                                <button
                                  onClick={() => startCallDoc(alert, 'מוקד חירום רשות מקומית', '106', 'CalledLocalEmergency')}
                                  disabled={isUpdating}
                                  style={{
                                    padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.orangeBorder}`,
                                    backgroundColor: colors.orangeLight, cursor: 'pointer', fontSize: '12px',
                                    display: 'flex', alignItems: 'center', gap: '4px', color: colors.orange,
                                  }}
                                >
                                  <Landmark size={12} /> מוקד חירום רשות מקומית
                                </button>
                              </div>
                            </div>

                            {/* Call Documentation Inline Form */}
                            <CallDocumentModal
                              callDoc={callDoc}
                              alert={alert}
                              isUpdating={isUpdating}
                              onNoteChange={(note) => callDoc && setCallDoc({ ...callDoc, note })}
                              onSave={() => saveCallDoc(alert)}
                              onClose={() => setCallDoc(null)}
                            />

                            {/* Messaging Buttons */}
                            <div style={{ marginBottom: '12px' }}>
                              <p style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px' }}>שליחת הודעה:</p>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() => startMessaging(alert.alertId, 'WhatsApp')}
                                  disabled={isUpdating}
                                  style={{
                                    padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`,
                                    backgroundColor: colors.white, cursor: 'pointer', fontSize: '12px',
                                    display: 'flex', alignItems: 'center', gap: '4px',
                                  }}
                                >
                                  <MessageSquare size={12} color="#25D366" /> WhatsApp
                                </button>
                                <button
                                  onClick={() => startMessaging(alert.alertId, 'SMS')}
                                  disabled={isUpdating}
                                  style={{
                                    padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`,
                                    backgroundColor: colors.white, cursor: 'pointer', fontSize: '12px',
                                    display: 'flex', alignItems: 'center', gap: '4px',
                                  }}
                                >
                                  <MessageSquare size={12} color={colors.info} /> SMS
                                </button>
                                <button
                                  onClick={() => startMessaging(alert.alertId, 'Email')}
                                  disabled={isUpdating}
                                  style={{
                                    padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`,
                                    backgroundColor: colors.white, cursor: 'pointer', fontSize: '12px',
                                    display: 'flex', alignItems: 'center', gap: '4px',
                                  }}
                                >
                                  <Mail size={12} color={colors.warning} /> Email
                                </button>
                              </div>
                            </div>

                            {/* Messaging Flow — Contact Picker + Documentation */}
                            <AnimatePresence>
                              {messagingFlow && messagingFlow.alertId === alert.alertId && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  style={{
                                    padding: '14px', borderRadius: '10px',
                                    backgroundColor: colors.infoLight, border: `1px solid ${colors.infoBorder}`,
                                    marginBottom: '12px', overflow: 'hidden',
                                  }}
                                >
                                  {messagingFlow.step === 'pickContact' ? (
                                    <>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <p style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                                          בחר נמען - שליחת {messagingFlow.method}
                                        </p>
                                        <button onClick={() => setMessagingFlow(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px' }}>
                                          <X size={16} color={colors.textLight} />
                                        </button>
                                      </div>
                                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        {alert.parentContacts.map((parent, i) => (
                                          <button
                                            key={i}
                                            onClick={() => selectMessagingContact(parent.name, parent.phone)}
                                            style={{
                                              padding: '8px 14px', borderRadius: '8px',
                                              border: `1px solid ${colors.border}`, backgroundColor: colors.white,
                                              cursor: 'pointer', fontSize: '13px',
                                              display: 'flex', alignItems: 'center', gap: '6px',
                                            }}
                                          >
                                            <User size={13} /> {parent.name}
                                          </button>
                                        ))}
                                        {messagingFlow.method !== 'Email' && (
                                          <button
                                            onClick={() => selectMessagingContact(alert.kindergartenName, alert.kindergartenPhone)}
                                            style={{
                                              padding: '8px 14px', borderRadius: '8px',
                                              border: `1px solid ${colors.border}`, backgroundColor: colors.white,
                                              cursor: 'pointer', fontSize: '13px',
                                              display: 'flex', alignItems: 'center', gap: '6px',
                                            }}
                                          >
                                            <Building2 size={13} /> {alert.kindergartenName}
                                          </button>
                                        )}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <p style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                                          <Send size={14} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: '4px' }} />
                                          {messagingFlow.method} ל-{messagingFlow.contactName}
                                        </p>
                                        <button onClick={() => setMessagingFlow(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px' }}>
                                          <X size={16} color={colors.textLight} />
                                        </button>
                                      </div>
                                      <textarea
                                        value={messagingFlow.note}
                                        onChange={(e) => setMessagingFlow({ ...messagingFlow, note: e.target.value })}
                                        placeholder="תוכן ההודעה / תיעוד (חובה)..."
                                        rows={3}
                                        style={{
                                          width: '100%', padding: '10px', borderRadius: '8px',
                                          border: `1px solid ${colors.border}`, fontSize: '13px',
                                          direction: 'rtl', resize: 'vertical', boxSizing: 'border-box',
                                          marginBottom: '8px',
                                        }}
                                        autoFocus
                                      />
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                          onClick={() => saveMessaging(alert)}
                                          disabled={isUpdating || !messagingFlow.note.trim()}
                                          style={{
                                            padding: '8px 16px', borderRadius: '8px', border: 'none',
                                            backgroundColor: messagingFlow.note.trim() ? colors.primary : colors.mutedLight,
                                            color: messagingFlow.note.trim() ? colors.white : colors.muted,
                                            cursor: messagingFlow.note.trim() ? 'pointer' : 'not-allowed',
                                            fontSize: '13px', fontWeight: '600',
                                            display: 'flex', alignItems: 'center', gap: '4px',
                                          }}
                                        >
                                          <Send size={14} /> שמור תיעוד
                                        </button>
                                        <button
                                          onClick={() => setMessagingFlow({ ...messagingFlow, step: 'pickContact', note: '' })}
                                          style={{
                                            padding: '8px 16px', borderRadius: '8px',
                                            border: `1px solid ${colors.border}`, backgroundColor: colors.white,
                                            cursor: 'pointer', fontSize: '13px', color: colors.textLight,
                                          }}
                                        >
                                          חזור
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>

                            {/* Free text note — with gray bg when active (not in closing mode) */}
                            <div style={{
                              padding: '14px', borderRadius: '10px',
                              border: `1px solid ${colors.border}`,
                              backgroundColor: closingAlertId !== alert.alertId ? colors.mutedLight : 'transparent',
                              marginBottom: '16px',
                            }}>
                              <p style={{ fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
                                הוסף הערה:
                              </p>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <textarea
                                  value={actionNote}
                                  onChange={(e) => setActionNote(e.target.value)}
                                  placeholder="הערה חופשית / תיעוד נוסף..."
                                  rows={3}
                                  style={{
                                    flex: 1, padding: '10px 12px', borderRadius: '8px',
                                    border: `1px solid ${colors.border}`, fontSize: '13px',
                                    direction: 'rtl', resize: 'vertical', boxSizing: 'border-box',
                                    backgroundColor: colors.white,
                                  }}
                                />
                                <button
                                  onClick={() => { if (actionNote.trim()) handleAddAction(alert, 'Note', actionNote.trim()); }}
                                  disabled={isUpdating || !actionNote.trim()}
                                  style={{
                                    padding: '10px 14px', borderRadius: '8px', border: 'none',
                                    backgroundColor: actionNote.trim() ? colors.primary : colors.mutedLight,
                                    color: actionNote.trim() ? colors.white : colors.muted,
                                    cursor: actionNote.trim() ? 'pointer' : 'not-allowed',
                                    fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px',
                                    marginTop: '0',
                                  }}
                                >
                                  <FileText size={14} /> הוסף הערה
                                </button>
                              </div>
                            </div>

                            {/* Button to open closing section */}
                            {closingAlertId !== alert.alertId && (
                              <div style={{ marginBottom: '16px' }}>
                                <button
                                  onClick={() => setClosingAlertId(alert.alertId)}
                                  style={{
                                    padding: '10px 20px', borderRadius: '8px',
                                    border: `1px solid ${colors.border}`,
                                    backgroundColor: colors.white, cursor: 'pointer',
                                    fontSize: '13px', fontWeight: '600', color: colors.text,
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                  }}
                                >
                                  <CheckCircle size={14} color={colors.success} /> סגור התראה
                                </button>
                              </div>
                            )}

                            {/* Resolution — visible only when closing mode is active */}
                            {closingAlertId === alert.alertId && (
                              <div style={{
                                padding: '14px', borderRadius: '10px',
                                border: `1px solid ${colors.border}`, backgroundColor: colors.mutedLight,
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                  <FormLabel text="הערות פתרון" helpText={callCenterHelpTexts.resolutionNotes.tooltip} style={{ color: colors.text }} />
                                  <button
                                    onClick={() => setClosingAlertId(null)}
                                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px', color: colors.textLight }}
                                  >
                                    ✕
                                  </button>
                                </div>
                                <textarea
                                  value={resolutionText}
                                  onChange={(e) => setResolutionText(e.target.value)}
                                  placeholder="פרט את תוצאת הטיפול (חובה)..."
                                  rows={3}
                                  style={{
                                    width: '100%', padding: '10px', borderRadius: '8px',
                                    border: `1px solid ${colors.border}`, fontSize: '13px',
                                    direction: 'rtl', resize: 'vertical', boxSizing: 'border-box',
                                    backgroundColor: colors.white,
                                  }}
                                />
                                <HelperText text={callCenterHelpTexts.resolutionNotes.helper!} />
                                <FormLabel text="סוג פתרון" helpText={callCenterHelpTexts.resolutionType.tooltip} style={{ color: colors.text, marginTop: '10px' }} />
                                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                  <button
                                    onClick={() => handleResolve(alert, 'Resolved')}
                                    disabled={isUpdating || !resolutionText.trim()}
                                    style={{
                                      flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
                                      backgroundColor: resolutionText.trim() ? colors.success : colors.mutedLight,
                                      color: resolutionText.trim() ? colors.white : colors.muted,
                                      cursor: resolutionText.trim() ? 'pointer' : 'not-allowed',
                                      fontSize: '14px', fontWeight: '600',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                    }}
                                  >
                                    <CheckCircle size={16} /> טופל
                                  </button>
                                  <button
                                    onClick={() => handleResolve(alert, 'FalseAlarm')}
                                    disabled={isUpdating || !resolutionText.trim()}
                                    style={{
                                      flex: 1, padding: '12px', borderRadius: '8px',
                                      border: `1px solid ${colors.border}`,
                                      backgroundColor: resolutionText.trim() ? colors.mutedLight : colors.mutedLight,
                                      color: resolutionText.trim() ? colors.text : colors.muted,
                                      cursor: resolutionText.trim() ? 'pointer' : 'not-allowed',
                                      fontSize: '14px', fontWeight: '600',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                    }}
                                  >
                                    <XCircle size={16} /> אזעקת שווא
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ===== ACTION LOG (Timeline) — sorted chronologically ===== */}
                        {alert.actionLog && alert.actionLog.length > 0 && (() => {
                          // מיזוג כל האירועים למערך אחד וממוין כרונולוגית
                          type TimelineEntry = {
                            time: string;
                            type: 'created' | 'pickedUp' | 'action' | 'resolved';
                            label?: string;
                            color: string;
                            log?: typeof alert.actionLog[0];
                          };
                          const timelineEntries: TimelineEntry[] = [
                            { time: alert.alertTime, type: 'created' as const, label: 'התראה נוצרה', color: colors.info },
                            ...(alert.pickedUpAt ? [{
                              time: alert.pickedUpAt,
                              type: 'pickedUp' as const,
                              label: `נלקח לטיפול${alert.assignedTo ? ` ע"י ${alert.assignedTo}` : ''}`,
                              color: colors.warning,
                            }] : []),
                            ...alert.actionLog.map(log => ({
                              time: log.timestamp,
                              type: 'action' as const,
                              log,
                              color: (log.userId === 0 || log.userName === 'מערכת') ? colors.info : colors.primary,
                            })),
                            ...(alert.resolvedAt ? [{
                              time: alert.resolvedAt,
                              type: 'resolved' as const,
                              label: alert.status === 'FalseAlarm' ? 'סומן כאזעקת שווא' : 'טופל ונסגר',
                              color: alert.status === 'FalseAlarm' ? colors.muted : colors.success,
                            }] : []),
                          ].sort((a, b) => {
                            // השוואה ישירה של HH:mm:ss מתוך ISO string — מונע בעיות timezone (Z vs ללא Z)
                            const timeA = a.time?.match(/T(\d{2}:\d{2}:\d{2})/)?.[1] || a.time?.match(/T(\d{2}:\d{2})/)?.[1] || '';
                            const timeB = b.time?.match(/T(\d{2}:\d{2}:\d{2})/)?.[1] || b.time?.match(/T(\d{2}:\d{2})/)?.[1] || '';
                            return timeA.localeCompare(timeB);
                          });

                          return (
                          <div style={{ marginTop: '0' }}>
                            <p style={{ fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '10px' }}>
                              ציר זמן פעולות:
                            </p>
                            <div style={{
                              borderRight: `2px solid ${colors.border}`,
                              paddingRight: '16px',
                              marginRight: '8px',
                            }}>
                              {timelineEntries.map((entry, i) => {
                                const isLast = i === timelineEntries.length - 1;
                                if (entry.type === 'action' && entry.log) {
                                  const isSystem = entry.log.userId === 0 || entry.log.userName === 'מערכת';
                                  return (
                                    <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: isLast ? '0' : '12px', position: 'relative' }}>
                                      <div style={{
                                        position: 'absolute', right: '-24px', top: '2px',
                                        width: '12px', height: '12px', borderRadius: '50%',
                                        backgroundColor: entry.color, border: `2px solid ${colors.white}`,
                                      }} />
                                      <div style={{ fontSize: '12px' }}>
                                        <span style={{ color: colors.textLight }}>{formatTime(entry.log.timestamp)}</span>
                                        {' — '}
                                        {isSystem ? (
                                          <span style={{ fontWeight: '600', color: colors.info }}>
                                            {translateAction(entry.log.action)}
                                          </span>
                                        ) : (
                                          <span style={{ fontWeight: '600', color: colors.text }}>
                                            {entry.log.userName}
                                          </span>
                                        )}
                                        {': '}
                                        <span style={{ color: colors.text }}>{entry.log.details}</span>
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: isLast ? '0' : '12px', position: 'relative' }}>
                                    <div style={{
                                      position: 'absolute', right: '-24px', top: '2px',
                                      width: '12px', height: '12px', borderRadius: '50%',
                                      backgroundColor: entry.color, border: `2px solid ${colors.white}`,
                                    }} />
                                    <div style={{ fontSize: '12px' }}>
                                      <span style={{ color: colors.textLight }}>{formatTime(entry.time)}</span>
                                      {' — '}
                                      <span style={{ fontWeight: '600', color: entry.color }}>
                                        {entry.label}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          );
                        })()}

                        {/* Simple action log for cards with no timeline entries but with resolution */}
                        {(!alert.actionLog || alert.actionLog.length === 0) && isCompleted && (
                          <div style={{ fontSize: '13px', color: colors.textLight }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <Clock size={14} />
                              <span>נוצר: {formatTime(alert.alertTime)}</span>
                              {alert.resolvedAt && <span> → נסגר: {formatTime(alert.resolvedAt)}</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
